import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import SearchableSelect from "@/components/SearchableSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const EMPTY_FORM = { name: "", company: "", email: "", phone: "", address: "" };

export default function CustomerPicker({
  value = "",
  onChange,
  onCustomerChange,
  placeholder = "Search or select customer...",
  testId = "customer-picker",
  clearable = true,
  canCreate = true,
  stackAddButton = false,
  className = "",
}) {
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const loadCustomers = async () => {
    try {
      const { data } = await api.get("/customers");
      setCustomers(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  useEffect(() => {
    loadCustomers();
  }, []);

  const selectCustomer = (customerId) => {
    onChange(customerId);
    onCustomerChange?.(customers.find((customer) => customer.customer_id === customerId) || null);
  };

  const createCustomer = async () => {
    if (!form.name.trim()) {
      toast.error("Customer name is required");
      return;
    }
    try {
      setSaving(true);
      const { data } = await api.post("/customers", {
        ...form,
        name: form.name.trim(),
        company: form.company.trim() || null,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        address: form.address.trim() || null,
      });
      setCustomers((current) => [...current, data].sort((a, b) => a.name.localeCompare(b.name)));
      onChange(data.customer_id);
      onCustomerChange?.(data);
      setForm(EMPTY_FORM);
      setOpen(false);
      toast.success("Customer registered");
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const options = customers.filter((customer) => customer.active !== false).map((customer) => ({
    value: customer.customer_id,
    label: customer.company ? `${customer.name} · ${customer.company}` : customer.name,
    hint: customer.phone || customer.email || "",
  }));

  return (
    <div className={`${stackAddButton ? "space-y-1" : "flex items-center gap-2"} w-full min-w-0 ${className}`}>
      <div className={stackAddButton ? "min-w-0 w-full" : "min-w-0 flex-1"}>
        <SearchableSelect
          value={value}
          onChange={selectCustomer}
          options={options}
          placeholder={placeholder}
          searchPlaceholder="Search customers by name or contact..."
          clearable={clearable}
          testId={testId}
          emptyMessage="No customer found. Register a new customer."
        />
      </div>
      {canCreate && (
        <Button type="button" variant="outline" size="sm" className={stackAddButton ? "ml-auto flex h-7" : ""} onClick={() => setOpen(true)} data-testid={`${testId}-add`}>
          <Plus className="mr-1.5 h-4 w-4" /> Add
        </Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`${testId}-dialog`}>
          <DialogHeader>
            <DialogTitle>Register customer</DialogTitle>
            <DialogDescription>Save this customer to the shared customer database.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label htmlFor={`${testId}-name`}>Customer name *</Label>
              <Input id={`${testId}-name`} autoFocus value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} data-testid={`${testId}-name`} />
            </div>
            <div>
              <Label htmlFor={`${testId}-company`}>Company</Label>
              <Input id={`${testId}-company`} value={form.company} onChange={(event) => setForm({ ...form, company: event.target.value })} data-testid={`${testId}-company`} />
            </div>
            <div>
              <Label htmlFor={`${testId}-phone`}>Phone</Label>
              <Input id={`${testId}-phone`} value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} data-testid={`${testId}-phone`} />
            </div>
            <div>
              <Label htmlFor={`${testId}-email`}>Email</Label>
              <Input id={`${testId}-email`} type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} data-testid={`${testId}-email`} />
            </div>
            <div>
              <Label htmlFor={`${testId}-address`}>Address</Label>
              <Input id={`${testId}-address`} value={form.address} onChange={(event) => setForm({ ...form, address: event.target.value })} data-testid={`${testId}-address`} />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="button" onClick={createCustomer} disabled={saving} data-testid={`${testId}-save`}>
              {saving ? "Saving..." : "Register customer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
